import type { ApiResponse, Branch, BranchType } from '@packages/shared-types';
import { X_TENANT_ID_HEADER } from '@packages/shared-utils';

import { apiClient } from '@/services/apiClient';

export type CreateBranchPayload = {
  name: string;
  type: BranchType;
  address?: string;
  tenantId?: string;
};

export type UpdateBranchPayload = {
  name?: string;
  type?: BranchType;
  address?: string;
};

export interface ListBranchesPaginatedParams {
  page: number;
  limit: number;
}

export interface ListBranchesPaginatedResult {
  data: Branch[];
  total: number;
  page: number;
  limit: number;
}

function branchesHeaders(tenantId?: string | null): Record<string, string> | undefined {
  return tenantId ? { [X_TENANT_ID_HEADER]: tenantId } : undefined;
}

export async function listBranchesPaginated(
  params: ListBranchesPaginatedParams,
  tenantId?: string | null,
): Promise<ListBranchesPaginatedResult> {
  const { page, limit } = params;
  const response = await apiClient.get<ApiResponse<ListBranchesPaginatedResult>>(
    '/api/branches',
    { params: { page, limit }, headers: branchesHeaders(tenantId) },
  );
  const payload = response.data.data;
  return {
    data: payload.data,
    total: payload.total,
    page: payload.page,
    limit: payload.limit,
  };
}

export async function listBranches(tenantId?: string | null): Promise<Branch[]> {
  const response = await apiClient.get<ApiResponse<Branch[]>>('/api/branches', {
    headers: branchesHeaders(tenantId),
  });
  return response.data.data;
}

export async function getBranchById(id: string): Promise<Branch> {
  const response = await apiClient.get<ApiResponse<Branch>>(`/api/branches/${id}`);
  return response.data.data;
}

export async function createBranch(payload: CreateBranchPayload): Promise<Branch> {
  const response = await apiClient.post<ApiResponse<Branch>>('/api/branches', payload);
  return response.data.data;
}

export async function updateBranch(
  id: string,
  payload: UpdateBranchPayload,
): Promise<Branch> {
  const response = await apiClient.put<ApiResponse<Branch>>(
    `/api/branches/${id}`,
    payload,
  );
  return response.data.data;
}

export async function deleteBranch(id: string): Promise<void> {
  await apiClient.delete<ApiResponse<void>>(`/api/branches/${id}`);
}
